<template>
  <article class="flex h-full min-w-0 flex-col">
    <header class="m-2 flex-none  px-6 py-4">
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-xs font-bold uppercase text-primary">{{ section.canonicalRef }}</p>
          <h1 class="mt-1 text-xl font-bold leading-tight text-gray-800">{{ section.title }}</h1>
        </div>
        <div class="flex flex-none flex-wrap items-center justify-end gap-2">
          <span class="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">{{ section.amendmentKey }}</span>
          <span class="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-bold uppercase text-gray-600">{{ section.type }}</span>
          <RouterLink :to="compareRoute" class="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-bold text-gray-700 hover:bg-gray-50">
            Compare amendments
          </RouterLink>
          <button
            v-if="isAdmin"
            type="button"
            :class="editMode ? 'success' : 'primary'"
            class="!m-0 !px-2 !py-1 !text-xs"
            @click="editMode = !editMode"
          >
            <font-awesome-icon icon="pen" class="mr-1" />
            {{ editMode ? "Finish Editing" : "Edit Paragraphs" }}
          </button>
        </div>
      </div>
      <div v-if="section.breadcrumb?.length" class="mt-2 text-xs text-gray-400">{{ section.breadcrumb.join(" / ") }}</div>

      <details v-if="section.needsReview && reviewIssues.length" class="mt-3 border border-yellow-300 bg-yellow-50 text-gray-900">
        <summary class="cursor-pointer px-3 py-2 text-xs font-bold">
          Review required: {{ reviewIssues.length }} data quality {{ reviewIssues.length === 1 ? "issue" : "issues" }}
        </summary>
        <div class="border-t border-yellow-200 px-3">
          <div v-for="issue in reviewIssues" :key="issue.rawReason" class="border-b border-yellow-200 py-3 text-xs last:border-b-0">
            <p class="font-bold">{{ issue.title }}</p>
            <p class="mt-1 leading-relaxed">{{ issue.meaning }}</p>
            <p class="mt-1 leading-relaxed"><strong>Recommended action:</strong> {{ issue.action }}</p>
            <p v-if="issue.detail" class="mt-1 font-mono text-xs text-yellow-800">Reported value: {{ issue.detail }}</p>
            <button
              v-if="isAdmin"
              type="button"
              :disabled="completingReason === issue.rawReason"
              class="!m-0 mt-2 !rounded !border !border-yellow-400 !bg-white !px-2 !py-1 !text-xs !font-bold !text-gray-900 disabled:opacity-50"
              @click="completeIssue(issue.rawReason)"
            >
              {{ completingReason === issue.rawReason ? "Saving..." : issue.code === "low_text_coverage" ? "Approve as reviewed" : "Mark issue complete" }}
            </button>
          </div>
          <p v-if="actionError" class="border-t border-red-200 py-2 text-xs font-bold text-red-700">{{ actionError }}</p>
        </div>
      </details>
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5 bg-gray-50 shadow-inset">
      <p v-if="!section.paragraphs?.length" class="max-w-4xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        This version has no paragraph records. The indexed section data is incomplete.
      </p>
      <div class="max-w-[960px] mx-auto ">
        <ParagraphBlock
          v-for="(paragraph, index) in section.paragraphs"
          :key="paragraph.paragraphId || index"
          :paragraph="paragraph"
          :assets="assetPlacement.paragraphAssets[index]"
          :edit-mode="editMode"
          @edit="openParagraphEditor"
        />
      </div>
      <div v-if="assetPlacement.unreferencedAssets.length" class="mt-8 max-w-4xl border-t border-gray-200 pt-4">
        <p class="mb-2 text-xs font-bold uppercase text-gray-400">Unreferenced assets</p>
        <AssetCard v-for="asset in assetPlacement.unreferencedAssets" :key="asset.assetId" :asset="asset" />
      </div>
    </div>
    <DynamicForm ref="paragraphForm" />
  </article>
</template>

<script setup>
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import { useStore } from "vuex";
import { appConfig } from "@@/db.js";

import DynamicForm from "@@/components/DynamicForm/DynamicForm.vue";
import AssetCard from "./AssetCard.vue";
import ParagraphBlock from "./ParagraphBlock.vue";
import { useRegulationsState } from "../state.js";
import { assignSectionAssets } from "../utils/sectionAssets.js";

const props = defineProps({ section: { type: Object, required: true } });
const store = useStore();
const regs = useRegulationsState();
const completingReason = ref("");
const actionError = ref("");
const editMode = ref(false);
const paragraphForm = ref(null);
const isAdmin = computed(() => store.state.user?.Security?.General?.ApplicationAdmin === true);
const assetPlacement = computed(() => assignSectionAssets(props.section.paragraphs, props.section.assets));
const compareRoute = computed(() => ({
  name: "regulations-compare",
  params: {
    sectionKey: props.section.sectionKey,
    type: props.section.type,
    qualifier: props.section.qualifier || "_",
  },
  query: {
    amendment: props.section.amendmentNumber,
    partNumber: props.section.partNumber,
    versionId: props.section.versionId,
  },
}));

const ISSUE_INFO = {
  empty_extraction: ["No text was extracted", "The source pages were found, but extraction produced no usable text.", "Confirm the page range and rerun extraction."],
  source_segment_missing: ["Source segment is missing", "The section cannot be matched to its original extracted segment.", "Regenerate the version index and rerun validation."],
  source_text_hash_mismatch: ["Source text changed after processing", "The source no longer matches the hash recorded during processing.", "Reprocess the section from the updated source."],
  schema_invalid: ["Section structure is invalid", "One or more fields do not match the required schema.", "Correct the reported fields, then rerun validation and indexing."],
  bad_label: ["Paragraph label was not recognised", "A label does not follow a supported numbering pattern.", "Compare with the source and correct the label or paragraph boundary."],
  bad_path: ["Paragraph hierarchy is invalid", "A paragraph path contains an unsupported value.", "Correct the paragraph path against the source and revalidate."],
  unresolved_refs: ["Cross-references could not be resolved", "One or more references do not match an indexed section.", "Correct malformed targets and re-index the section."],
  missing_assets: ["Referenced assets are missing", "A figure, table, or formula has no matching asset record.", "Rerun visual extraction or repair the paragraph asset references."],
  asset_metadata_missing: ["Asset metadata is incomplete", "An asset is missing its image path, source page, or bounding box.", "Regenerate the affected asset metadata."],
  orphan_assets: ["Asset image is unavailable", "An asset record exists but its image file is missing.", "Restore or regenerate the image and rerun validation."],
  low_text_coverage: ["Extracted text may be incomplete", "Less than 98% of source text was found in the assembled section.", "Compare with the source, restore omissions, and rerun validation."],
};
const reviewIssues = computed(() => (props.section.reviewReasons || []).map((rawReason) => {
  const separator = rawReason.indexOf(":");
  const code = (separator < 0 ? rawReason : rawReason.slice(0, separator)).trim();
  const rawDetail = separator < 0 ? "" : rawReason.slice(separator + 1).trim();
  const info = ISSUE_INFO[code] || [code.replaceAll("_", " "), "The validation pipeline reported an unrecognised condition.", "Inspect the validation report before approving this section."];
  const coverage = Number(rawDetail);
  const detail = code === "low_text_coverage" && Number.isFinite(coverage)
    ? `${(coverage * 100).toFixed(1)}% source coverage (minimum 98%)`
    : rawDetail;
  return { rawReason, code, detail, title: info[0], meaning: info[1], action: info[2] };
}));

async function completeIssue(reason) {
  completingReason.value = reason;
  actionError.value = "";
  try {
    await regs.completeReviewIssue(reason);
  } catch (error) {
    actionError.value = error?.message || "Failed to complete the review issue.";
  } finally {
    completingReason.value = "";
  }
}

function openParagraphEditor(paragraph) {
  if (!isAdmin.value || !paragraph?.paragraphId) return;
  paragraphForm.value.setConfig({
    title: `Edit paragraph ${paragraph.label || paragraph.order + 1}`,
    type: "function",
    functionName: "updateRegParagraph",
    submitButtonLabel: "Save Paragraph",
    successMessage: "Paragraph updated",
    fields: [{
      label: "Paragraph text",
      name: "text",
      type: "multiline",
      rows: 12,
      divClass: "col-span-3",
      markdownMathPreview: true,
      tooltip: "Supports Markdown tables and LaTeX using \\(inline\\), \\[display\\], or $$display$$ delimiters.",
    }],
    preSubmitHook() {
      return {
        databaseURL: appConfig?.firebaseConfig?.databaseURL,
        versionId: props.section.versionId,
        paragraphId: paragraph.paragraphId,
        expectedText: paragraph.text,
      };
    },
    postSubmitHook({ functionResult }) {
      regs.replaceParagraph(functionResult.paragraph);
    },
  });
  paragraphForm.value.setValues({ text: paragraph.text });
  paragraphForm.value.toggle();
}
</script>
